import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type CSSProperties,
  type SetStateAction,
} from 'react';
import { LearnViewportContext } from '@/app/LearnViewportContext';

export const WORKING_PANEL = {
  TUTOR: 'tutor',
  TEXT: 'text',
  MANAGEMENT: 'management',
  PREFERENCES: 'preferences',
} as const;
export type WorkingPanel = (typeof WORKING_PANEL)[keyof typeof WORKING_PANEL] | null;
interface OverlayState {
  panel: WorkingPanel;
  setPanel: Dispatch<SetStateAction<WorkingPanel>>;
  fullScreen: boolean;
  focusTrapQuery?: string;
  openFullScreen?: () => void;
  portalTarget?: HTMLElement | null;
  sheetStyle?: CSSProperties;
  shortViewport?: boolean;
}
export const WorkingOverlayContext = createContext<OverlayState | null>(null);
export function useWorkingOverlay() {
  const provided = useContext(WorkingOverlayContext);
  const [panel, setPanel] = useState<WorkingPanel>(null);
  return provided ?? { panel, setPanel, fullScreen: false };
}
// The visual viewport follows the software keyboard without changing the saved paper size.
export function useWorkingViewport(fullScreen: boolean) {
  const container = useContext(LearnViewportContext);
  const read = useCallback(() => {
    const top = window.visualViewport?.offsetTop ?? 0;
    const height = window.visualViewport?.height ?? window.innerHeight;
    if (!container) return { height, top };
    const rect = container.getBoundingClientRect();
    const visibleTop = Math.max(rect.top, top);
    return {
      height: Math.max(0, Math.min(rect.bottom, top + height) - visibleTop),
      top: Math.max(0, visibleTop - rect.top),
    };
  }, [container]);
  const [viewport, setViewport] = useState(read);
  useEffect(() => {
    if (!fullScreen) return;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const previous = meta?.getAttribute('content');
    if (meta && !previous?.includes('viewport-fit'))
      meta.setAttribute(
        'content',
        `${previous ?? 'width=device-width,initial-scale=1'},viewport-fit=cover`,
      );
    return () => {
      if (meta && previous !== undefined) {
        if (previous === null) meta.removeAttribute('content');
        else meta.setAttribute('content', previous);
      }
    };
  }, [fullScreen]);
  useEffect(() => {
    const update = () => setViewport(read());
    update();
    const observer =
      container && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (container) observer?.observe(container);
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [container, read]);
  return viewport;
}
